import React, { useState, useMemo } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { 
  Image as ImageIcon, 
  FileText, 
  Download, 
  ExternalLink,
  File
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

interface Attachment {
  url: string;
  messageId: string;
  senderName: string;
  sentAt: string;
}

interface MessageAttachmentsPanelProps {
  attachments: Attachment[];
}

type FilterType = 'all' | 'images' | 'documents';

const isImageUrl = (url: string): boolean => {
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url);
};

const getFileName = (url: string): string => {
  const parts = url.split('/');
  const filename = parts[parts.length - 1];
  // Remove timestamp prefix if present (e.g., "1234567890-abc123.pdf" -> "abc123.pdf")
  const cleanedFilename = filename.replace(/^\d+-[a-z0-9]+-/, '');
  return decodeURIComponent(cleanedFilename);
};

const getFileExtension = (url: string): string => {
  const match = url.match(/\.([a-zA-Z0-9]+)$/);
  return match ? match[1].toUpperCase() : 'FILE';
};

export const MessageAttachmentsPanel: React.FC<MessageAttachmentsPanelProps> = ({ attachments }) => {
  const [filter, setFilter] = useState<FilterType>('all');

  const filteredAttachments = useMemo(() => {
    switch (filter) {
      case 'images':
        return attachments.filter(a => isImageUrl(a.url));
      case 'documents':
        return attachments.filter(a => !isImageUrl(a.url));
      default:
        return attachments;
    }
  }, [attachments, filter]);

  const imageCount = useMemo(() => 
    attachments.filter(a => isImageUrl(a.url)).length, 
    [attachments]
  );

  const documentCount = useMemo(() => 
    attachments.filter(a => !isImageUrl(a.url)).length, 
    [attachments]
  );

  if (attachments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <File className="h-12 w-12 mb-3 opacity-50" />
        <p className="text-sm">No attachments shared yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Tabs */}
      <div className="flex gap-2">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
        >
          All ({attachments.length})
        </Button>
        <Button
          variant={filter === 'images' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('images')}
          disabled={imageCount === 0}
        >
          <ImageIcon className="h-4 w-4 mr-1" />
          Images ({imageCount})
        </Button>
        <Button
          variant={filter === 'documents' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('documents')}
          disabled={documentCount === 0}
        >
          <FileText className="h-4 w-4 mr-1" />
          Documents ({documentCount})
        </Button>
      </div>

      <ScrollArea className="h-[350px]">
        {/* Image Grid */}
        {(filter === 'all' || filter === 'images') && imageCount > 0 && (
          <div className="mb-6">
            {filter === 'all' && documentCount > 0 && (
              <h4 className="text-sm font-medium mb-3 text-muted-foreground">Images</h4>
            )}
            <div className="grid grid-cols-3 gap-2">
              {filteredAttachments
                .filter(a => isImageUrl(a.url))
                .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())
                .map((attachment, index) => (
                  <a
                    key={`${attachment.messageId}-${index}`}
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative aspect-square rounded-lg overflow-hidden bg-muted hover:ring-2 hover:ring-primary transition-all"
                  >
                    <img
                      src={attachment.url}
                      alt={getFileName(attachment.url)}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <ExternalLink className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-1 bg-gradient-to-t from-black/60 to-transparent">
                      <p className="text-[10px] text-white truncate">
                        {format(parseISO(attachment.sentAt), 'MMM d')}
                      </p>
                    </div>
                  </a>
                ))}
            </div>
          </div>
        )}

        {/* Documents List */}
        {(filter === 'all' || filter === 'documents') && documentCount > 0 && (
          <div>
            {filter === 'all' && imageCount > 0 && (
              <h4 className="text-sm font-medium mb-3 text-muted-foreground">Documents</h4>
            )}
            <div className="space-y-2">
              {filteredAttachments
                .filter(a => !isImageUrl(a.url))
                .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())
                .map((attachment, index) => (
                  <div
                    key={`${attachment.messageId}-${index}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                  >
                    <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-primary/10 text-primary shrink-0">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {getFileName(attachment.url)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {attachment.senderName} • {format(parseISO(attachment.sentAt), 'MMM d, yyyy')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-muted-foreground px-2 py-0.5 rounded bg-background">
                        {getFileExtension(attachment.url)}
                      </span>
                      <a
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-md hover:bg-background transition-colors"
                      >
                        <Download className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};
